import { GridRowData } from '@material-ui/data-grid';
import { AssetEntity, EntityType } from '../../../util/enums';
import { AssetState } from '../../../util/Events/Messages';
import { PortableAssetDto } from '../../../API/types/metadata';
import { stringifyIdRecord } from '../../../util/stringUtils';
import { Asset } from '../types';

/**
 * Universal selection model for Live/Replay.
 *
 * A single discriminated union replaces the previously independent
 * selectedBeacon / selectedDevice / selectedGPSTracker / selectedTool /
 * selectedGeofence / selectedMicrofence / selectedActiveAsset state values, so
 * only one entity can be highlighted at a time. `selectedLayer` and
 * `selectedStringifiedGeofenceEventData` remain separate co-existing fields as
 * they are filter/sub-selection context rather than the highlighted entity.
 */

export type AssetSelectionType = EntityType.Beacon | EntityType.Device | EntityType.GpsTracker;

export type SelectedAssetData = { asset: Asset & { type: AssetEntity }; activeAsset?: AssetState };
export type SelectedToolData = { asset: PortableAssetDto; activeAsset?: AssetState };
export type SelectedMicrofenceData = { microfence: GridRowData; activeAsset?: AssetState };
export type SelectedActiveAssetData = { activeAsset: AssetState };

export type SelectedEntity =
  | { type: EntityType.Beacon; id: string; label: string; data: SelectedAssetData }
  | { type: EntityType.Device; id: string; label: string; data: SelectedAssetData }
  | { type: EntityType.GpsTracker; id: string; label: string; data: SelectedAssetData }
  | { type: EntityType.Tool; id: string; label: string; data: SelectedToolData }
  | { type: EntityType.Asset; id: string; label: string; data: SelectedActiveAssetData }
  | { type: EntityType.Geofence; id: string; label: string; data: GridRowData }
  | { type: EntityType.Microfence; id: string; label: string; data: SelectedMicrofenceData };

/**
 * Distinguishes PortableAssetDto from a plain map Asset.
 * Asset.ids is string when present; PortableAssetDto.ids is object or null when present.
 * When ids is absent, require a positive DTO signal (uuid) so Tool Assets with
 * optional properties still take the Asset mapping branch in toolSelection.
 */
const isPortableAssetDto = (asset: Asset | PortableAssetDto): asset is PortableAssetDto => {
  if (asset.type !== AssetEntity.Tool) return false;
  if (typeof asset.ids === 'string') return false;

  const hasDtoIds = asset.ids === null || typeof asset.ids === 'object';
  const hasUuid = 'uuid' in asset && asset.uuid !== undefined;
  if (!hasDtoIds && !hasUuid) return false;

  return asset.properties !== undefined && typeof asset.properties === 'object';
};

// --- Builders -------------------------------------------------------------

export const assetSelection = (
  type: AssetSelectionType,
  asset: Asset,
  activeAsset?: AssetState,
): SelectedEntity => {
  const assetEntityType =
    type === EntityType.Beacon
      ? AssetEntity.Beacon
      : type === EntityType.Device
      ? AssetEntity.Device
      : AssetEntity.GpsTracker;

  return {
    type,
    id: asset.id,
    label: asset.label,
    data: { asset: { ...asset, type: assetEntityType }, activeAsset },
  };
};

export const toolSelection = (
  asset: Asset | PortableAssetDto,
  activeAsset?: AssetState,
): SelectedEntity => {
  const portableAsset: PortableAssetDto = isPortableAssetDto(asset)
    ? asset
    : {
        id: asset.id,
        label: asset.label,
        tool: asset.tool,
        isArchived: asset.isArchived,
        archivedAt: asset.archivedAt,
        derived: asset.derived,
        model: asset.model,
        properties: asset.properties ?? {},
        type: AssetEntity.Tool,
      };

  return {
    type: EntityType.Tool,
    id: portableAsset.id,
    label: portableAsset.label ?? '',
    data: {
      asset: portableAsset,
      activeAsset,
    },
  };
};

export const geofenceSelection = (geofence: GridRowData): SelectedEntity => ({
  type: EntityType.Geofence,
  id: geofence.id,
  label: geofence.name ?? geofence.label ?? '',
  data: geofence,
});

export const microfenceSelection = (
  microfence: GridRowData,
  activeAsset?: AssetState,
): SelectedEntity => ({
  type: EntityType.Microfence,
  id: microfence.id,
  label: microfence.name ?? microfence.label ?? '',
  data: { microfence, activeAsset },
});

export const activeAssetSelection = (activeAsset: AssetState): SelectedEntity => ({
  type: EntityType.Asset,
  id: stringifyIdRecord(activeAsset.id),
  label: activeAsset.label ?? '',
  data: { activeAsset },
});

// --- Selectors (narrowing) ------------------------------------------------

export const selectedBeacon = (selected?: SelectedEntity): Asset | undefined =>
  selected?.type === EntityType.Beacon ? selected.data.asset : undefined;

export const selectedDevice = (selected?: SelectedEntity): Asset | undefined =>
  selected?.type === EntityType.Device ? selected.data.asset : undefined;

export const selectedGPSTracker = (selected?: SelectedEntity): Asset | undefined =>
  selected?.type === EntityType.GpsTracker ? selected.data.asset : undefined;

export const selectedTool = (selected?: SelectedEntity): PortableAssetDto | undefined =>
  selected?.type === EntityType.Tool ? selected.data.asset : undefined;

export const selectedAsset = (selected?: SelectedEntity): Asset | undefined => {
  if (!selected) return undefined;
  switch (selected.type) {
    case EntityType.Beacon:
    case EntityType.Device:
    case EntityType.GpsTracker:
      return selected.data.asset;
    case EntityType.Tool:
      return {
        id: selected.data.asset.id,
        label: selected.data.asset.label ?? '',
        type: selected.data.asset.type,
        tool: selected.data.asset.tool,
        isArchived: selected.data.asset.isArchived,
        archivedAt: selected.data.asset.archivedAt,
        properties: selected.data.asset.properties,
        derived: selected.data.asset.derived,
        model: selected.data.asset.model,
      };
    default:
      return undefined;
  }
};

export const selectedGeofence = (selected?: SelectedEntity): GridRowData | undefined =>
  selected?.type === EntityType.Geofence ? selected.data : undefined;

export const selectedMicrofence = (selected?: SelectedEntity): GridRowData | undefined =>
  selected?.type === EntityType.Microfence ? selected.data.microfence : undefined;

export const selectedActiveAsset = (selected?: SelectedEntity): AssetState | undefined => {
  if (!selected) return undefined;
  switch (selected.type) {
    case EntityType.Beacon:
    case EntityType.Device:
    case EntityType.GpsTracker:
    case EntityType.Tool:
    case EntityType.Microfence:
    case EntityType.Asset:
      return selected.data.activeAsset;
    default:
      return undefined;
  }
};

// True when a highlighted entity is selected. Mirrors the previous
// `somethingSelected` semantics, which excluded a lone active-asset selection.
export const isSomethingSelected = (selected?: SelectedEntity): boolean =>
  !!selected && selected.type !== EntityType.Asset;

export const deselectEntity = (
  selected: SelectedEntity | undefined,
  preserveTypes: readonly EntityType[],
): SelectedEntity | undefined =>
  selected && preserveTypes.includes(selected.type) ? selected : undefined;

// Returns a copy of the selection with a refreshed activeAsset, used to keep
// the selection in sync with the live stream. Clearing the activeAsset of a
// pure active-asset selection clears the selection entirely.
export const withActiveAsset = (
  selected: SelectedEntity | undefined,
  activeAsset: AssetState | undefined,
): SelectedEntity | undefined => {
  if (!selected) return selected;
  switch (selected.type) {
    case EntityType.Beacon:
    case EntityType.Device:
    case EntityType.GpsTracker:
      return { ...selected, data: { ...selected.data, activeAsset } };
    case EntityType.Tool:
      return { ...selected, data: { ...selected.data, activeAsset } };
    case EntityType.Microfence:
      return { ...selected, data: { ...selected.data, activeAsset } };
    case EntityType.Asset:
      return activeAsset ? { ...selected, data: { activeAsset } } : undefined;
    default:
      return selected;
  }
};
